/**
 * @fileoverview App Initialization Service
 * Handles centralized initialization of user and settings data
 * Ensures single fetch and proper state management
 */

import { authApi } from '../API/authAPI.ts';
import { settingsApi } from '../API/settingsAPI.ts';
import { appStateProxy } from '../hooks/useAppState.ts';
import { AppSettingsState } from '../pages/globalState/AppSettingsState.ts';

// Track if initialization is in progress to prevent duplicate calls
let initializationPromise: Promise<boolean> | null = null;

/**
 * Initialize app state by fetching user and settings
 * This function ensures only one fetch happens at a time
 * 
 * @returns Promise<boolean> - true if initialization successful, false otherwise
 */
export const initializeAppState = async (): Promise<boolean> => {
    // If logged out (after manual logout), don't re-initialize until login
    if (isLoggedOut) {
        console.debug('AppInitializer: Skipping initialization - user logged out');
        return Promise.resolve(false);
    }
    
    // If already initialized successfully with user data, skip re-initialization
    if (appStateProxy.isInitialized && appStateProxy.user) {
        return Promise.resolve(true);
    }
    
    // If already initializing, return the existing promise
    if (initializationPromise) {
        return initializationPromise;
    }
    
    // Start initialization
    appStateProxy.isInitializing = true;
    appStateProxy.initError = null;
    
    // Create the initialization promise
    initializationPromise = (async () => {
        try {
            // Fetch user first. If the user is not authenticated, skip settings
            // fetch to avoid noisy 401 requests during app bootstrap.
            const userResult = await authApi.getCurrentUser();

            if (userResult.success && userResult.data) {
                // User is logged in
                isLoggedOut = false; // Clear logged out flag on successful login
                appStateProxy.user = userResult.data;

                const settingsResult = await settingsApi.getAppSettings().catch((err) => {
                    console.error('AppInitializer: Settings fetch error', err);
                    return { success: false, error: String(err) };
                });

                if (settingsResult.success && settingsResult.data) {
                    appStateProxy.settings = settingsResult.data as any;
                } else {
                    console.warn('AppInitializer: Failed to load settings', settingsResult.error);
                }

                appStateProxy.isInitialized = true;
                appStateProxy.isInitializing = false;
                initializationPromise = null;
                return true;

            } else {
                // User not logged in or fetch failed
                appStateProxy.initError = userResult.error || 'Not authenticated';
                appStateProxy.isInitialized = true; // Mark as initialized even on failure
                appStateProxy.isInitializing = false;
                initializationPromise = null;
                return false;
            }
            
        } catch (error) {
            // Network or other error
            console.error('AppInitializer: Initialization error', error);
            appStateProxy.initError = error instanceof Error ? error.message : 'Unknown error';
            appStateProxy.isInitialized = true; // Mark as initialized even on error
            appStateProxy.isInitializing = false;
            initializationPromise = null;
            return false;
        }
    })();
    
    return initializationPromise;
};

// Flag to track if we're in a logged out state (to prevent re-initialization)
let isLoggedOut = false;

/**
 * Clears the logged-out guard after a successful login so initialization can run.
 */
export const clearLoggedOutState = () => {
    isLoggedOut = false;
};

/**
 * Reset the initialization state
 * Use this on logout or when you need to re-initialize
 * Note: isInitialized stays true to allow immediate redirect to login page
 */
export const resetInitialization = () => {
    console.debug('AppInitializer: Resetting initialization state');
    isLoggedOut = true; // Mark as logged out
    initializationPromise = null;
    appStateProxy.user = undefined;
    appStateProxy.settings = undefined;
    appStateProxy.isInitialized = true; // Keep true to allow redirect
    appStateProxy.isInitializing = false;
    appStateProxy.initError = null;
    AppSettingsState.isInitialized = false;
};
